import { useMemo } from 'react';
import type { CalendarPropertyRow, CalendarResponse } from '@/shared/api';
import { planColumn } from './calendarBarText';
import { nestCalendarRows } from './calendarFamily';
import type { ChannelSlug, StayFilter } from './calendarHelpers';
import { buildRowModel, rowIsDoubleBooked, type CalendarRowModel } from './calendarRowModel';

export interface CalendarRowLayout {
  row: CalendarPropertyRow;
  isRoom: boolean;
  model: CalendarRowModel;
}

interface Filters {
  selectedProperties: Set<string> | null;
  enabledChannels: Set<ChannelSlug>;
  stayFilter: StayFilter;
  today: string;
}

/**
 * Die sichtbaren Zeilen mit ihrem Modell und was die Legende davon erklären muss. Einmal gerechnet,
 * damit Raster und Legende nie auseinanderlaufen.
 */
export function useCalendarRows(data: CalendarResponse | undefined, filters: Filters) {
  const { selectedProperties, enabledChannels, stayFilter, today } = filters;
  return useMemo(() => {
    const rows = data?.properties ?? [];
    const shown = selectedProperties
      ? rows.filter((row) => selectedProperties.has(row.propertyId))
      : rows;
    const layouts: CalendarRowLayout[] = nestCalendarRows(shown).map(({ row, isRoom }) => ({
      row,
      isRoom,
      model: buildRowModel(row, { enabledChannels, stayFilter, today }),
    }));
    const days = data?.properties[0]?.cells.length ?? 0;
    return {
      layouts,
      dates: data?.properties[0]?.cells.map((cell) => cell.date) ?? [],
      legend: {
        family: rows.some((row) => row.flatFamily),
        doubleBooked: layouts.some(({ model }) => rowIsDoubleBooked(model)),
        plan: layouts.some(({ model }) =>
          model.bars.some(({ span }) => planColumn(span, data?.from ?? '', days) !== null),
        ),
      },
    };
  }, [data, selectedProperties, enabledChannels, stayFilter, today]);
}

export type CalendarRows = ReturnType<typeof useCalendarRows>;
